import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import MonthRangePicker from "@/components/MonthRangePicker";

interface MultiSelectProps {
  label: string;
  options: string[];
  availableOptions?: string[];
  selected: string[];
  onToggle: (v: string) => void;
  searchable?: boolean;
  
  labelFor?: (value: string) => string;
}

export const MultiSelect = ({ label, options, selected, onToggle, searchable, availableOptions = options, labelFor = (v) => v }: MultiSelectProps & { availableOptions?: string[] }) => {
  const [open, setOpen] = useState(false);
  const isActive = selected.length > 0;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`flex items-center gap-1.5 h-8 px-3 rounded-lg transition-all duration-150 ${
            isActive ? "bg-primary/10 border-primary/50 text-primary hover:bg-primary/15" : "hover:bg-accent"
          }`}
        >
          <span className="font-medium">{label}</span>
          {isActive && (
            <Badge variant="secondary" className="px-1.5 py-0 text-xs min-w-[20px] justify-center bg-primary/20 text-primary border-0">
              {selected.length}
            </Badge>
          )}
          <ChevronsUpDown className="h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          {searchable && <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />}
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map(opt => {
                const isSelected = selected.includes(opt);
                const unavailable = isSelected && !availableOptions.includes(opt);
                return (
                <CommandItem key={opt} value={opt} onSelect={() => (isSelected || !unavailable) && onToggle(opt)} className={`flex items-center gap-2 ${unavailable ? "cursor-pointer text-muted-foreground" : "cursor-pointer"}`}>
                  <div className={`flex h-4 w-4 items-center justify-center rounded border ${selected.includes(opt) ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                    {selected.includes(opt) && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="truncate">{labelFor(opt)}{unavailable && <span className="block text-[10px]">Selected — no matching data with other active filters.</span>}</span>
                </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export interface FilterFieldDef {
  key: string;
  label: string;
  options: string[];
  availableOptions?: string[];
  selected: string[];
  searchable?: boolean;
  labelFor?: (value: string) => string;
}

interface GenericFilterBarProps {
  fields: FilterFieldDef[];
  onToggle: (key: string, value: string) => void;
  onClearAll: () => void;
  min_month: string | null;
  max_month: string | null;
  date_from: string | null;
  date_to: string | null;
  onDateChange: (from: string | null, to: string | null) => void;
  isLoading?: boolean;
  hasActiveFilters?: boolean;
}

const GenericFilterBar = ({
  fields,
  onToggle,
  onClearAll,
  min_month,
  max_month,
  date_from,
  date_to,
  onDateChange,
  isLoading,
  hasActiveFilters,
}: GenericFilterBarProps) => {
  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
      <div className="h-0.5 bg-secondary">
        <div className={`h-full bg-primary transition-all duration-500 ${isLoading ? "opacity-100 w-full animate-pulse" : "opacity-0 w-0"}`} />
      </div>
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {fields.map(f => (
            <MultiSelect
              key={f.key}
              label={f.label}
              options={f.options}
              availableOptions={f.availableOptions}
              selected={f.selected}
              onToggle={v => onToggle(f.key, v)}
              searchable={f.searchable}
              labelFor={f.labelFor}
            />
          ))}
          <div className="h-5 w-px bg-border/70 mx-1 shrink-0" />
          {min_month && max_month ? (
            <MonthRangePicker
              min_month={min_month}
              max_month={max_month}
              date_from={date_from}
              date_to={date_to}
              onChange={onDateChange}
            />
          ) : (
            <span className="text-xs text-muted-foreground">Loading…</span>
          )}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearAll}
              className="h-8 gap-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0 ml-auto"
            >
              <X className="h-3.5 w-3.5" />
              Clear all
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenericFilterBar;
